import { onClickOutside, onKeyStroke } from "@vueuse/core";
import { tabbable } from "tabbable";
import { computed, type Ref } from "vue";

export const useInteractions = ({
  isToggletip,
  visible,
  anchor,
  tooltipArrow,
  tooltip,
}: {
  isToggletip: Ref<boolean>;
  visible: Ref<boolean>;
  anchor: Ref<HTMLElement | undefined>;
  tooltipArrow: Ref<HTMLElement | undefined>;
  tooltip: Ref<HTMLElement | undefined>;
}) => {
  // --- Mouse handling ---
  const handleMouseOver = () => {
    if (isToggletip.value) return;
    visible.value = true;
  };

  const tooltipParts = computed(() => [anchor, tooltipArrow, tooltip]);
  const elementIsPartOfTooltip = (element: Node) =>
    tooltipParts.value.some((part) => part.value?.contains(element));

  const handleMouseOut = (e: MouseEvent | FocusEvent) => {
    if (isToggletip.value) return;
    if (elementIsPartOfTooltip(e.relatedTarget as Node)) return;
    visible.value = false;
  };

  // --- Click handling ---
  const handleClick = () => {
    if (!isToggletip.value) return;
    visible.value = !visible.value;
  };

  const handleClickOutside = () => {
    visible.value = false;
  };

  onClickOutside(tooltip, handleClickOutside, {
    ignore: [anchor, tooltipArrow],
  });

  // --- Key handling ---
  onKeyStroke("Escape", (e) => {
    e.preventDefault();
    handleClickOutside();
  });

  const getTabbableInElement = (element: Ref<HTMLElement | undefined>) => {
    if (!element.value) return;

    const tabbables = tabbable(element.value);
    if (tabbables.length === 0) return;

    // We only support exactly one anchor button and one optional interactive element in the footer, so it's safe to use the first element here
    return tabbables[0];
  };

  const moveFocusToTooltip = (event: KeyboardEvent) => {
    const tabbableElementInTooltip = getTabbableInElement(tooltip);
    if (!tabbableElementInTooltip) return;

    event.preventDefault();
    tabbableElementInTooltip.focus();
  };

  const handleAnchorKeydown = async (event: KeyboardEvent) => {
    if (event.key === "Tab" && !event.shiftKey && visible.value) {
      moveFocusToTooltip(event);
    }
  };

  const moveFocusToAnchorButtonOrNextElement = (event: KeyboardEvent) => {
    const anchorButton = getTabbableInElement(anchor);

    if (event.shiftKey) {
      event.preventDefault();
      anchorButton!.focus();
      if (!isToggletip.value) visible.value = false;
      return;
    }

    // We are intentionally not using preventDefault here:
    // This will shortly focus the anchorButton and then the next interactive element via the default event handling.
    anchorButton!.focus();
  };

  const handleTooltipKeydown = async (event: KeyboardEvent) => {
    if (event.key === "Tab") moveFocusToAnchorButtonOrNextElement(event);
  };

  return {
    handleMouseOver,
    handleMouseOut,
    handleClick,
    handleAnchorKeydown,
    handleTooltipKeydown,
  };
};
